import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { useI18n } from '../i18n'
import { UsernameField } from '../components/UsernameField'
import { Meta } from '../components/Meta'

type Tab = 'login' | 'register'

interface Props {
  initialTab?: Tab
}

export function LoginPage({ initialTab = 'login' }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const register = useAuthStore((s) => s.register)
  const error = useAuthStore((s) => s.error)
  const clearError = useAuthStore((s) => s.clearError)
  const isLoading = useAuthStore((s) => s.isLoading)

  const [tab, setTab] = useState<Tab>(initialTab)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')

  const switchTab = (t: Tab) => {
    setTab(t)
    clearError()
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    const ok = await login(email, password)
    if (ok) navigate('/')
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirmPassword) {
      return
    }
    const ok = await register(username, email, password)
    if (ok) navigate('/')
  }

  return (
    <>
    <Meta title={t('meta.login.title')} description={t('meta.login.description')} path="/login" />
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Tab Switcher */}
        <div className="mb-6 flex rounded-lg bg-surface-light p-1">
          <button
            onClick={() => switchTab('login')}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-colors ${
              tab === 'login'
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {t('auth.login')}
          </button>
          <button
            onClick={() => switchTab('register')}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-colors ${
              tab === 'register'
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {t('auth.register')}
          </button>
        </div>


        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg bg-error/10 p-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* Login Form */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input
              type="email"
              placeholder={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-lg bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {t('auth.login')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-text-muted hover:text-primary transition-colors"
            >
              {t('auth.forgotPassword')}
            </button>
            <p className="text-center text-sm text-text-muted">
              {t('auth.noAccount')}{' '}
              <button
                type="button"
                onClick={() => switchTab('register')}
                className="text-primary hover:underline"
              >
                {t('auth.register')}
              </button>
            </p>
          </form>
        )}

        {/* Register Form */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="flex flex-col gap-4">
            <UsernameField value={username} onChange={setUsername} />
            <input
              type="email"
              placeholder={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-error">{t('auth.passwordMismatch')}</p>
            )}
            <button
              type="submit"
              disabled={isLoading || password !== confirmPassword}
              className="rounded-lg bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {t('auth.register')}
            </button>
            <p className="text-center text-sm text-text-muted">
              {t('auth.hasAccount')}{' '}
              <button
                type="button"
                onClick={() => switchTab('login')}
                className="text-primary hover:underline"
              >
                {t('auth.login')}
              </button>
            </p>
          </form>
        )}
        {/* Freeware notice */}
        <div className="mt-8 rounded-xl border border-surface-lighter bg-surface-light p-5 text-center">
          <p className="mb-1 font-semibold text-text">{t('login.noticeTitle')}</p>
          <p className="mb-3 text-sm text-text-muted">{t('login.noticeText')}</p>
          <a
            href="https://paypal.me/masterlevelDE"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
          >
            {t('login.noticeCta')}
          </a>
        </div>

      </div>
    </div>
    </>
  )
}
